import React, { useMemo, useState } from 'react';
import { format } from 'date-fns';
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { matchSorter } from 'match-sorter';
import { GridCellParams, GridColDef } from '@mui/x-data-grid';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import UploadIcon from '@mui/icons-material/Upload';
import { DataTable, RowData } from '@/components/shared/Table/DataTable';
import { UploadedFileCard } from './UploadedFileCard';
import { IdmsUploadsQuery, useLatestIdmsUploadsByTypeQuery } from '@/__generated__/graphql';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import IdmsTask from './IdmsTask';

type Props = {
  filesData: IdmsUploadsQuery | undefined;
  structureId: string;
  fileInputReference: React.MutableRefObject<HTMLInputElement | null | undefined>;
  setTemplatesModal: React.Dispatch<React.SetStateAction<boolean>>;
};

export const FilesListing = ({
  filesData,
  structureId,
  fileInputReference,
  setTemplatesModal,
}: Props) => {
  const [searchFile, setSearchFile] = useState<string>('');
  const [updatedRows, setUpdatedRows] = useState<RowData[]>([]);
  const { data } = useLatestIdmsUploadsByTypeQuery({
    variables: { structureId },
  });

  const filesListingData: RowData[] = useMemo(() => {
    const listingData = (filesData?.idmsUploads ?? []).map(({ id, name, createdAt, filePath }) => ({
      id,
      fileName: name,
      uploadedOn: format(Number(createdAt), 'MM/dd/yyyy'),
      fileUrl: filePath,
    }));
    setUpdatedRows(listingData);

    return listingData;
  }, [filesData?.idmsUploads]);

  const recentlyUploadedFiles = useMemo(() => {
    return (data?.latestIdmsUploadsByType ?? []).map(({ name, createdAt, filePath }) => ({
      fileName: name,
      uploadedOn: format(Number(createdAt), 'MM/dd/yyyy'),
      fileUrl: filePath,
    }));
  }, [data?.latestIdmsUploadsByType]);

  const columns: GridColDef[] = [
    {
      field: 'fileName',
      headerName: 'FILE NAME',
      flex: 1,
      width: 200,
      renderCell: (parameters: GridCellParams<RowData>) => (
        <Box height="100%" sx={{ display: 'flex', alignItems: 'center' }}>
          <img src="/assets/upload_file_icon.svg" width={24} height={24} />
          <Typography ml={2}>{parameters.row.fileName}</Typography>
        </Box>
      ),
    },
    {
      field: 'uploadedOn',
      headerName: 'UPLOADED ON',
      flex: 1,
      width: 150,
    },
    {
      field: 'actions',
      headerName: '',
      align: 'right',
      flex: 1,
      width: 50,
      renderCell: (file) => (
        <Link href={getCloudfrontUrl(file.row.fileUrl)}>
          <IconButton>
            <FileDownloadOutlinedIcon sx={{ color: 'action.selected' }} />
          </IconButton>
        </Link>
      ),
    },
  ];

  const handleSearchFile = (searchTerm: string) => {
    setSearchFile(searchTerm);
    if (filesListingData) {
      const filteredRows = filesListingData.filter((row) =>
        row.fileName.toString().toLowerCase().includes(searchTerm.toLowerCase())
      );

      setUpdatedRows(filteredRows);
    }
  };

  return (
    <Box mb={5}>
      <Stack
        sx={{ flexDirection: 'row', justifyContent: 'space-between' }}
        data-testid="files-listing"
      >
        <Typography color="text.primary" component="h1" fontSize="3.2rem" fontWeight={700}>
          Fabric Uploads
        </Typography>
        <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ alignItems: 'center' }}>
          <Button
            id="idms-template-download-button"
            variant="outlined"
            onClick={() => setTemplatesModal(true)}
          >
            Download Templates
          </Button>
          <Button
            id="idms-upload-file-button"
            variant="contained"
            startIcon={<UploadIcon />}
            onClick={() => fileInputReference.current?.click()}
          >
            Upload file
          </Button>
        </Stack>
      </Stack>
      <Stack sx={{ pt: 2 }}>
        <Typography color="text.primary" component="h1" fontSize="1.6rem" fontWeight={700}>
          Latest uploads
        </Typography>
        <Typography variant="body1" color="action.selected" fontSize="1.4rem">
          These are the latest uploads for each file type on Abyss fabric.
        </Typography>
      </Stack>
      <Grid container spacing={5} mt={2} mb={4}>
        {recentlyUploadedFiles.map((file) => (
          <Grid item lg={2.5} md={2.5} sm={6} xs={12}>
            <UploadedFileCard
              name={file.fileName}
              uploadedOn={file.uploadedOn}
              fileUrl={file.fileUrl}
            />
          </Grid>
        ))}
      </Grid>

      <Box
        flex={1}
        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', mb: 4 }}
      >
        <Autocomplete
          options={[...new Set(filesListingData.map((list) => list.fileName))]}
          onInputChange={(event, newValue) => handleSearchFile(newValue || '')}
          freeSolo
          forcePopupIcon
          clearOnEscape
          filterOptions={(options, { inputValue }) => matchSorter(options, inputValue)}
          renderInput={(parameters) => (
            <TextField
              {...parameters}
              data-testid="search-file-input"
              sx={{
                width: { xs: '100%', xl: '640px' },
              }}
              size="small"
              value={searchFile}
              placeholder="Search a file"
              variant="outlined"
              InputProps={{
                ...parameters.InputProps,
                disableUnderline: true,
                startAdornment: (
                  <InputAdornment position="start" sx={{ marginLeft: '8px' }}>
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
      </Box>

      <DataTable rows={updatedRows} columns={columns} pageSizeOptions={[10, 20, 50]} />
      <IdmsTask />
    </Box>
  );
};
